import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ValueUpload } from "./ValueUpload";
import { ValuePickRow } from "./ValuePickRow";
import { ValueHistory } from "./ValueHistory";
import { ValueBankroll } from "./ValueBankroll";
import { ManagerLink } from "./manager/ManagerLink";
import { CheckOdds } from "./CheckOdds";
import { ValueTiers, inTier } from "./ValueTiers";
import { VIEWS, ValueCards, ValueMarkets, ViewSwitch } from "./ValueViews";
import { pickOutcome, pickProgress } from "./valueFormat";
import "./ValueScanner.css";

function Skipped({ items, priced }) {
  if (!items?.length) return null;
  const reasons = Object.entries(items.reduce((a, s) => ({ ...a, [s.reason]: (a[s.reason] || 0) + 1 }), {}));
  return (
    <>
      {priced === 0 && <div className="vs-check-head vs-none-saved" data-testid="value-none-saved">
        Nothing saved from this screenshot · {reasons.map(([r, n]) => `${n} × ${r}`).join(" · ")}</div>}
      <details className="vs-skipped" data-testid="value-skipped" open={priced === 0}>
        <summary>{items.length} prices skipped · {reasons.map(([r, n]) => `${n} ${r}`).join(" · ")}</summary>
        {items.map((s, i) => <div key={i}><span>{s.prop}</span><em>{s.reason}</em></div>)}
      </details>
    </>
  );
}

export function ValueScanner({ day, game, games, comp, valueCounts = {}, onChange, liveStats = {} }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [view, setView] = useState("today");
  const [scope, setScope] = useState("game");
  const [picks, setPicks] = useState(null);
  const [last, setLast] = useState(null);
  const [check, setCheck] = useState([]);
  const [tier, setTier] = useState(1);
  const [mode, setModeState] = useState(() => (VIEWS.includes(localStorage.getItem("value-view")) ? localStorage.getItem("value-view") : "table"));
  const setMode = (v) => { setModeState(v); localStorage.setItem("value-view", v); };
  useEffect(() => { if (game?.id) setScope("game"); }, [game?.id]);
  const tierPicks = useMemo(() => picks?.filter((p) => inTier(p, tier)) ?? null, [picks, tier]);
  const shown = useMemo(() => (tierPicks && scope === "game" && game ? tierPicks.filter((p) => p.game_id === game.id) : tierPicks), [tierPicks, scope, game]);
  const status = useMemo(() => Object.fromEntries((games || []).map((g) => [g.id, g.status])), [games]);
  const withOutcome = (shown || []).map((p) => {
    const live = liveStats[`${p.game_id}-${p.player_id}`];
    return { ...p, stake: null, stake_pct: null, outcome: pickOutcome(p, live, status[p.game_id]), progress: pickProgress(p, live, status[p.game_id]) };
  });
  const load = useCallback(() => api.get("/value/picks", { params: { day, comp } }).then((r) => { setPicks(r.data.picks); setCheck(r.data.check || []); return r.data.picks; }).catch((e) => { toast.error(errorText(e)); return null; }), [day, comp]);
  // Reload on day change, then keep in sync with the games-list value badges (which refresh on their own cadence)
  // so the scanner never lags behind a game that is already flagged as having value.
  useEffect(() => { setPicks(null); setLast(null); load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  // Clicking a flagged game must always show its picks: refresh if that game has a value badge but no loaded picks yet.
  useEffect(() => {
    if (game?.id && picks && (valueCounts[game.id] || 0) > picks.filter((p) => p.game_id === game.id).length) load();
  }, [game?.id, valueCounts, load]); // eslint-disable-line react-hooks/exhaustive-deps
  // If the selected game has picks but none in the current odds category, jump to a category that does.
  useEffect(() => {
    if (!picks || !game?.id || scope !== "game") return;
    const mine = picks.filter((p) => p.game_id === game.id);
    if (mine.length && !mine.some((p) => inTier(p, tier))) {
      const t = [1, 2, 3].find((id) => mine.some((p) => inTier(p, id)));
      if (t) setTier(t);
    }
  }, [picks, game?.id, scope]); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = async (id) => {
    try { await api.delete(`/value/picks/${id}`); onChange?.(await load()); } catch (e) { toast.error(errorText(e)); }
  };
  const confirm = async (id) => {
    try { await api.post(`/value/picks/${id}/confirm`); onChange?.(await load()); } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div className="vs-root" data-testid="value-scanner">
      <div className="tv-pills vs-tabs">
        <button type="button" className={view === "today" ? "is-active" : ""} onClick={() => setView("today")} data-testid="value-tab-today">Value picks</button>
        <button type="button" className={view === "history" ? "is-active" : ""} onClick={() => setView("history")} data-testid="value-tab-history">History</button>
        <button type="button" className={view === "bankroll" ? "is-active" : ""} onClick={() => setView("bankroll")} data-testid="value-tab-bankroll">Bankroll &amp; Kelly</button>
        {isAdmin && <button type="button" className={view === "upload" ? "is-active" : ""} onClick={() => setView("upload")} data-testid="value-tab-upload">
          Upload odds{check.length ? ` · ${check.length} to check` : ""}
        </button>}
      </div>
      {view === "history" && <ValueHistory game={game} comp={comp} />}
      {view === "bankroll" && (
        <>
          <ValueBankroll onSaved={load} />
          <p className="vs-foot">Your bankroll and Kelly choice set the suggested stake on every value pick. Each pick is capped at 5% of the bankroll.</p>
        </>
      )}
      {view === "upload" && isAdmin && (
        <>
          <ValueUpload day={day} games={games} comp={comp} selectedId={game?.id} onResult={(r) => { setLast(r); setPicks(r.picks); setCheck(r.check || []); onChange?.(r.picks); }} />
          {last && <p className="vs-note" data-testid="value-scan-summary">{last.scanned} prices read · {last.priced} priced · {last.value} value picks{last.flagged ? ` · ${last.flagged} to check` : ""}</p>}
          {last?.warnings?.length > 0 && <div className="vs-note" data-testid="value-scan-warnings">{last.warnings.map((w, i) => <div key={i}>{w}</div>)}</div>}
          <Skipped items={last?.skipped} priced={last?.priced} />
          {!comp && <ManagerLink day={day} refresh={last} />}
          <CheckOdds items={check} onConfirm={confirm} onDelete={remove} />
        </>
      )}
      {view === "today" && (
        <>
          <ValueTiers tier={tier} onChange={setTier} picks={scope === "game" && game ? picks?.filter((p) => p.game_id === game.id) : picks} />
          <div className="vs-ctrls">
            <div className="vs-block"><span className="vs-label">Display mode</span><ViewSwitch view={mode} onChange={setMode} /></div>
            {game && (
              <div className="vs-block"><span className="vs-label">Games</span>
                <div className="vs-seg">
                  <button type="button" className={scope === "game" ? "is-active" : ""} onClick={() => setScope("game")} data-testid="value-scope-game">
                    {game.away_code} @ {game.home_code} ({tierPicks?.filter((p) => p.game_id === game.id).length ?? 0})
                  </button>
                  <button type="button" className={scope === "all" ? "is-active" : ""} onClick={() => setScope("all")} data-testid="value-scope-all">
                    All games ({tierPicks?.length ?? 0})
                  </button>
                </div>
              </div>
            )}
          </div>
          {shown === null && <p className="tv-empty">Loading…</p>}
          {shown?.length === 0 && <p className="tv-empty" data-testid="value-empty">
            {scope === "game" && game && tierPicks?.length ? "No value picks for this game in this odds range. Switch to All games to see the rest." : `No value picks in this odds range yet${isAdmin ? "" : " — picks appear here once an admin scans the odds"}.`}
          </p>}
          {shown?.length > 0 && mode === "table" && withOutcome.map((p, i) => <ValuePickRow key={p.id} pick={p} rank={i + 1} onDelete={isAdmin ? remove : undefined} />)}
          {shown?.length > 0 && mode === "cards" && <ValueCards picks={withOutcome} onDelete={isAdmin ? remove : undefined} />}
          {shown?.length > 0 && mode === "markets" && <ValueMarkets picks={withOutcome} onDelete={isAdmin ? remove : undefined} />}
          <p className="vs-foot">Listed only with a real edge: EV 10%+ for odds up to 2.00, 15%+ up to 4.00, 20%+ above. Chance = model distribution blended with up to 40% recent-games hit rate (last 15). EV = chance × odds − 1. Not betting advice.</p>
        </>
      )}
    </div>
  );
}
